import { futureMoneyLogo } from "@assets/images";
import { AccountType } from "@ctypes";
import { useAcknowledgeCapitalGroupMigrationOverride, useModal, useTheme, useUser } from "@hooks";
import { i18n, Translations } from "@i18n";
import { FilledButton } from "@routes/DashboardRoute/Home/styles";
import { homeCssVars } from "@routes/DashboardRoute/Home/tokens";
import { Intercom } from "@services";
import { MigrationBalanceNotice, MigrationHeadline } from "./components";
import { Actions, Body, Logo, Note, Stack, Surface, TextAction } from "./styledComponents";

/**
 * One-time notice for users whose 529 moved under the 30-day notice without them approving it.
 * "Got it" clears the flag for good; messaging support deliberately does not, since that user is
 * disputing the move rather than acknowledging it.
 */
export const CapitalGroupMigrationOverrideModal = () => {
  const { dismiss } = useModal();
  const { theme } = useTheme();
  const { beneficiaries } = useUser();
  const [acknowledge, { loading: acknowledging }] = useAcknowledgeCapitalGroupMigrationOverride();

  // Name the child when exactly one has a 529. With several, naming one would imply the others
  // were not moved, so the copy falls back to a generic "your plan".
  const fiveTwoNineBeneficiaries =
    beneficiaries?.filter((beneficiary) =>
      beneficiary?.goals?.some((goal) =>
        goal?.accounts?.some((account) => account?.type === AccountType.FiveTwoNinePlan),
      ),
    ) ?? [];

  const beneficiaryName =
    fiveTwoNineBeneficiaries.length === 1 ? fiveTwoNineBeneficiaries[0]?.firstName : null;

  const body = beneficiaryName
    ? i18n.t(Translations.CapitalGroupMigrationOverrideBody, { beneficiary: beneficiaryName })
    : i18n.t(Translations.CapitalGroupMigrationOverrideBodyGeneric);

  const handleGotIt = async () => {
    try {
      await acknowledge();
    } finally {
      // Close regardless: at worst the notice shows once more next session, which beats trapping
      // the user on a dialog they have already read.
      dismiss();
    }
  };

  const handleMessageUs = () => {
    dismiss();
    Intercom.showNewMessage(i18n.t(Translations.CapitalGroupMigrationOverrideSupportMessage));
  };

  return (
    <Surface style={homeCssVars(theme.meta.dark)} role="dialog" aria-modal="true">
      <Stack>
        <Logo src={futureMoneyLogo} alt="" />
        <MigrationHeadline
          text={i18n.t(Translations.CapitalGroupMigrationOverrideTitle)}
          highlight={i18n.t(Translations.CapitalGroupMigrationOverrideTitleHighlight)}
        />
        <Body>{body}</Body>
        <MigrationBalanceNotice />
        <Note>{i18n.t(Translations.CapitalGroupMigrationContributionsUnchanged)}</Note>
      </Stack>

      <Actions>
        <FilledButton
          style={{ justifyContent: "center" }}
          onClick={handleGotIt}
          disabled={acknowledging}
          aria-busy={acknowledging}
        >
          {i18n.t(Translations.CapitalGroupMigrationOverridePrimaryCTA)}
        </FilledButton>
        <TextAction type="button" onClick={handleMessageUs}>
          {i18n.t(Translations.CapitalGroupMigrationOverrideSecondaryCTA)}
        </TextAction>
      </Actions>
    </Surface>
  );
};
